import React, {useState, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

// Chakra Components
import {
    useDisclosure,
    Button,
    Box,
    Flex,
    Stack,
    Skeleton,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import EmptyCart from '../../pages/cart/partials/empty-cart'
import ProductItem from '../../components/product-item'
import {
    CartRemoveButton,
    CartEditButton,
    CartAddToWishlistButton
} from '../../pages/cart/partials/cart-secondary-button-group'

// Hooks
import {useToast} from '../../hooks/use-toast'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST
} from '@salesforce/retail-react-app/app/constants'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import ProductViewModal from '../product-view-modal'
import BundleProductViewModal from '../product-view-modal/bundle'

import {useCustomToast} from '../../contexts/custom-toast-context'

import {sendItemCartEvent, sendWishlistEvent} from '../../utils/gtm-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const DEBOUNCE_WAIT = 750
const ProductItemList = ({setHasFinalSaleInCart, isMiniCart}) => {
    const {showCustomToast, setShowToastMessage} = useCustomToast()
    const {data: basket, isLoading} = useCurrentBasket()
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: false,
                perPricebook: true
            }
        },
        {
            enabled: Boolean(productIds),
            staleTime: 5 * 60 * 1000,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const config = getConfig()
    const configFinalSaleLabel = config.app.ENABLE_FINAL_SALE_LABEL === true || config.app.ENABLE_FINAL_SALE_LABEL === 'true'

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        let hasFinalSale = false

        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }

            let enableFinalSaleLabel = configFinalSaleLabel || currentProduct?.c_isEGiftCard || currentProduct?.c_isGiftCard

            // Check for final sale products
            if (currentProduct?.c_isFinalSale && enableFinalSaleLabel) {
                hasFinalSale = true
            }

            updateProductsByItemId[productItem.itemId] = currentProduct
        })

        // Set the hasFinalSaleInCart state
        setHasFinalSaleInCart && setHasFinalSaleInCart(hasFinalSale)

        return updateProductsByItemId
    }, [basket, products, bundleChildProductData, setHasFinalSaleInCart])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const navigate = useNavigation()
    const modalProps = useDisclosure()

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )

    const wishlistToastStyles = {
        position: 'relative',
        top: 'auto',
        bottom: 'auto',
        width: ['100%', 'auto'],
        minWidth: ['100%', '374px'],
        right: 0,
        left: 'auto',
        zIndex: 9999
    }

    const handleAddToWishlist = async (product) => {
        try {
            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.id
            )

            const isMaximumNumberOfItemsReached = wishlist?.customerProductListItems?.length >= 100

            if (!isItemInWishlist) {
                if (isMaximumNumberOfItemsReached) {
                    showCustomToast({
                        message: formatMessage(
                            {
                                id: 'cart.info.full_message',
                                defaultMessage:
                                    'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                            },
                            {
                                a: (chunks) => (
                                    <Link
                                        href="/account/wishlist"
                                        display="block"
                                        mt={2}
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }
                        ),
                        styles: wishlistToastStyles
                    })
                } else {
                    await createCustomerProductListItem.mutateAsync({
                        parameters: {
                            listId: wishlist.id,
                            customerId
                        },
                        body: {
                            // NOTE: APi does not respect quantity, it always adds 1
                            quantity: product.quantity,
                            productId: product.productId,
                            public: false,
                            priority: 1,
                            type: 'product'
                        }
                    })

                    const basketItem = basket?.productItems?.find(
                        (item) => item.productId === product?.id
                    )

                    // GTM Modification
                    sendWishlistEvent(product, false)

                    await removeItemFromBasketMutation.mutateAsync({
                        parameters: {
                            basketId: basket?.basketId,
                            itemId: basketItem.itemId
                        }
                    })
                }
            } else {
                toast({
                    title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    status: 'info',
                    action: (
                        <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                        </Button>
                    )
                })
            }
        } catch {
            showError()
        }
    }
    /**************** Wishlist ****************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            const productIds = basket.productItems.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    quantity,
                    price: variant.price
                }
                return await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: item
                })
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const basketItem = basket.productItems?.find(
                    ({productId}) => productId === variant.productId
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Show the modal.
            modalProps.onOpen()

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // GTM Modification
                    sendItemCartEvent(productsByItemId[product.itemId], true)
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                }
            }
        )
    }

    const refs = useRef([])

    /********* Show Cart removal toast and add border to the product **********/
    const cartRemoveToast = (index, product) => {
        const timeout = 3000

        if (refs.current[index]) {
            refs.current[index].style.borderColor = '#F9A456'
        }

        let undoClicked = false

        const timeoutId = setTimeout(() => {
            if (!undoClicked) {
                handleRemoveItem(product)
            }

            if (refs.current[index]) {
                refs.current[index].style.borderColor = 'white'
            }
        }, timeout)

        showCustomToast({
            message: formatMessage({
                id: 'cart.info.item_removed',
                defaultMessage: 'Item removed'
            }),
            action: (
                <Button
                    variant="link"
                    fontWeight="medium"
                    fontSize="sm"
                    onClick={() => {
                        undoClicked = true
                        clearTimeout(timeoutId)
                        if (refs.current[index]) {
                            refs.current[index].style.borderColor = 'white'
                        }
                        setShowToastMessage(false)
                    }}
                    colorScheme="white"
                    textDecoration="underline"
                >
                    {formatMessage({
                        id: 'cart_minicart.button.undo',
                        defaultMessage: 'Undo'
                    })}
                </Button>
            ),
            styles: {
                position: isMiniCart ? 'absolute' : 'fixed',
                top: isMiniCart ? '-48px' : 'auto',
                bottom: {base: 'auto', lg: isMiniCart ? 'auto' : '74px'},
                width: {base: '100%', lg: isMiniCart ? '100%' : '343px'},
                right: {base: '0', lg: isMiniCart ? '0' : 'auto'},
                left: {base: '0', lg: isMiniCart ? 0 : 8}
            },
            onClose: () => {
                handleRemoveItem(product)
            },
            containerId: isMiniCart ? 'mini-cart-toast-container' : 'app-header',
            duration: timeout
        })
    }

    /********* Rendering  UI **********/
    if (isLoading || isProductsLoading) {
        const itemCount = basket?.productItems?.length || 3
        return (
            <>
                {Array.from({length: itemCount}).map((_, i) => (
                    <Skeleton
                        key={i}
                        height="180px"
                        borderRadius="md"
                        mb={{base: '10px', lg: '14px'}}
                    />
                ))}
            </>
        )
    }

    if (!isLoading && !basket?.productItems?.length) {
        return <EmptyCart isRegistered={isRegistered} />
    }

    return (
        <>
            {[...(basket?.productItems ?? [])].reverse().map((productItem, idx) => {
                return (
                    <ProductItem
                        ref={(el) => (refs.current[idx] = el)}
                        key={productItem.itemId}
                        index={idx}
                        removeAction={
                            <CartRemoveButton
                                productName={productItem?.productName}
                                cartRemoveToast={() => cartRemoveToast(idx, productItem)}
                            />
                        }
                        editAction={
                            !productItem?.c_isEGiftCard &&
                            !productItem?.c_isGiftCard &&
                            !isMiniCart && (
                                <CartEditButton
                                    productName={productItem?.productName}
                                    onEditClick={(product) => {
                                        setSelectedItem(product)
                                        onOpen()
                                    }}
                                />
                            )
                        }
                        wishlistAction={
                            <CartAddToWishlistButton onAddToWishlistClick={handleAddToWishlist} />
                        }
                        product={{
                            ...productItem,
                            ...(productsByItemId && productsByItemId[productItem.itemId]),
                            isProductUnavailable: !isProductsLoading
                                ? !productsByItemId?.[productItem.itemId]
                                : undefined,
                            price: productItem.price,
                            priceAfterItemDiscount: productItem.priceAfterItemDiscount,
                            priceAfterOrderDiscount: productItem.priceAfterOrderDiscount,
                            basePrice: productItem.basePrice,
                            quantity: localQuantity[productItem.itemId]
                                ? localQuantity[productItem.itemId]
                                : productItem.quantity
                        }}
                        onItemQuantityChange={handleChangeItemQuantity.bind(this, productItem)}
                        showLoading={
                            isCartItemLoading && selectedItem?.itemId === productItem.itemId
                        }
                        handleRemoveItem={handleRemoveItem}
                        isMiniCart={isMiniCart}
                    />
                )
            })}
            <Box>
                {isOpen && !selectedItem.bundledProductItems && (
                    <ProductViewModal
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                        product={selectedItem}
                        updateCart={(variant, quantity) => handleUpdateCart(variant, quantity)}
                    />
                )}
                {isOpen && selectedItem.bundledProductItems && (
                    <BundleProductViewModal
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                        product={selectedItem}
                        updateCart={(product, quantity, childProducts) =>
                            handleUpdateBundle(product, quantity, childProducts)
                        }
                    />
                )}
            </Box>
        </>
    )
}

ProductItemList.propTypes = {
    isMiniCart: PropTypes.bool,
    setHasFinalSaleInCart: PropTypes.func
}

export default ProductItemList
